import { execAndWaitForAll } from "../../../Helpers/Functions/promise"
import { reportError } from "../../../Helpers/Functions/sentry"
import {
    APIOrgMember,
    APIOrgMemberRole
} from "../../../Models/API/APIOrgMember"
import { APIListContainer } from "../../../Models/API/Container"
import { hasMore } from "../../../Models/Client/RemoteData"
import { SortExpression } from "../../../Models/Client/SortExpression"
import { MainThunkDispatch, State } from "../../../Reducers"
import { apiEndpoints } from "../../../Routes/api"
import { failMessage, successMessage } from "../../Client/MessagesActions"
import { apiFetch } from "../../Client/NetworkActions"
import { OrgMemberType } from "../../Types"

export const listMyOrgs = () => async (dispatch: MainThunkDispatch) => {
    try {
        const endpoint = apiEndpoints.getMyOrgs({})
        const res = await dispatch(
            apiFetch<APIListContainer<APIOrgMember>>(endpoint.url, endpoint)
        )

        if (res === undefined) {
            return
        }

        dispatch({ type: OrgMemberType.setMyOrgs, payload: res.content })
    } catch (error) {
        reportError({ error })
    }
}

export const listOrgMembers =
    (orgUsername: string, page: number, sort: SortExpression<APIOrgMember>) =>
    async (dispatch: MainThunkDispatch) => {
        try {
            const endpoint = apiEndpoints.getOrgMembers({
                orgUsername,
                ...sort,
                page,
                pageSize: 30
            })
            const res = await dispatch(
                apiFetch<
                    Omit<APIListContainer<APIOrgMember>, "totalElements"> & {
                        total: number
                    }
                >(endpoint.url, endpoint)
            )

            if (res === undefined) {
                return
            }

            dispatch({
                type: OrgMemberType.setOrgMembers,
                meta: { orgUsername, page, total: res.total, sort },
                payload: res.content
            })
        } catch (error) {
            reportError({ error })
        }
    }

export const loadMoreOrgMembers =
    (orgUsername: string) =>
    async (dispatch: MainThunkDispatch, getState: () => State) => {
        const entry = getState().api.orgs.members[orgUsername]

        if (entry?.list === undefined || !hasMore(entry)) {
            return
        }

        await dispatch(
            listOrgMembers(
                orgUsername,
                entry.list.pages.length,
                entry.list.params
            )
        )
    }

export const reloadOrgMembers =
    (orgUsername: string) =>
    (dispatch: MainThunkDispatch, getState: () => State) => {
        const entry = getState().api.orgs.members[orgUsername]

        if (entry?.list === undefined) {
            return
        }

        dispatch(listOrgMembers(orgUsername, 0, entry.list.params))
    }

export const inviteToOrg =
    (orgUsername: string, emails: string[], role: APIOrgMemberRole) =>
    async (dispatch: MainThunkDispatch) => {
        try {
            const endpoint = apiEndpoints.inviteToOrg({ orgUsername })

            await execAndWaitForAll(
                emails.map(email =>
                    dispatch(
                        apiFetch<APIListContainer<APIOrgMember>>(
                            `${endpoint.url}${email}`,
                            endpoint,
                            {
                                method: "POST",
                                body: JSON.stringify({ email, role })
                            }
                        )
                    )
                )
            )

            dispatch(reloadOrgMembers(orgUsername))
            dispatch(
                successMessage(
                    emails.length > 1
                        ? "Invitations are sent"
                        : "Invitation is sent"
                )
            )
        } catch (error) {
            dispatch(
                failMessage(
                    emails.length > 1
                        ? "Could not send invitations"
                        : "Could not send invitation",
                    error
                )
            )
            throw error
        }
    }

export const changeOrgMemberRole =
    (orgUsername: string, id: number, role: APIOrgMemberRole) =>
    async (dispatch: MainThunkDispatch) => {
        try {
            const endpoint = apiEndpoints.updateMember({ orgUsername, id })

            await dispatch(
                apiFetch(endpoint.url, endpoint, {
                    method: "PATCH",
                    body: JSON.stringify({ role })
                })
            )

            dispatch(reloadOrgMembers(orgUsername))
            dispatch(successMessage("Member updated"))
        } catch (error) {
            dispatch(failMessage("Could not update member", error))
            throw error
        }
    }

export const removeMemberFromOrg =
    (orgUsername: string, id: number) =>
    async (dispatch: MainThunkDispatch) => {
        try {
            const endpoint = apiEndpoints.removeMemberFromOrg({
                orgUsername,
                id
            })

            await dispatch(
                apiFetch(endpoint.url, endpoint, { method: "DELETE" })
            )

            dispatch(reloadOrgMembers(orgUsername))
            dispatch(successMessage("Removed from organization"))
        } catch (error) {
            dispatch(failMessage("Could not remove from organization", error))
            throw error
        }
    }

export const acceptInvitationToOrg =
    (orgUsername: string, memberId: number, code: string) =>
    async (dispatch: MainThunkDispatch) => {
        try {
            const endpoint = apiEndpoints.acceptInvitationToOrg({
                orgUsername,
                memberId,
                code
            })

            await dispatch(apiFetch(endpoint.url, endpoint, { method: "POST" }))
        } catch (error) {
            dispatch(failMessage("Could not accept invitation", error))
            throw error
        }
    }

export const acceptInvitationAsWorker =
    (workerId: number) => async (dispatch: MainThunkDispatch) => {
        try {
            const endpoint = apiEndpoints.acceptInvitationAsWorker({
                workerId
            })

            await dispatch(apiFetch(endpoint.url, endpoint, { method: "POST" }))
        } catch (error) {
            dispatch(failMessage("Could not accept invitation", error))
            throw error
        }
    }
